import { Panel } from '@/components/Panel';
import { useKPI } from '@/hooks/useKPI';
import { Spinner, EmptyState, StatusBadge } from '@00start/ui';
import { formatDuration } from '@/utils/chart-helpers';

const MTTR_THRESHOLD_MS = 3_600_000;   // 1 hour
const MTTD_THRESHOLD_MS = 600_000;     // 10 minutes
const VOLUME_THRESHOLD = 10;
const RATE_THRESHOLD = 0.8;

interface StatCardProps {
  label: string;
  value: string;
  warn: boolean;
  warnLabel?: string;
}

function StatCard({ label, value, warn, warnLabel }: StatCardProps) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex flex-col gap-1">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-[22px] font-semibold text-slate-100 tabular-nums leading-tight">
        {value}
      </div>
      {warn && (
        <div className="mt-1">
          <StatusBadge status={warnLabel ?? 'warning'} variant="warning" />
        </div>
      )}
    </div>
  );
}

export function KPIDashboard() {
  const { kpis, loading } = useKPI();

  return (
    <Panel title="KPI Dashboard" mobilePriority={3}>
      {loading ? (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      ) : !kpis ? (
        <EmptyState title="No KPI data" description="KPI metrics unavailable." />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="MTTR"
            value={formatDuration(kpis.mttr_ms)}
            warn={kpis.mttr_ms > MTTR_THRESHOLD_MS}
            warnLabel="> 1h"
          />
          <StatCard
            label="MTTD"
            value={formatDuration(kpis.mttd_ms)}
            warn={kpis.mttd_ms > MTTD_THRESHOLD_MS}
            warnLabel="> 10m"
          />
          <StatCard
            label="Incidents"
            value={String(kpis.total_incidents)}
            warn={kpis.total_incidents > VOLUME_THRESHOLD}
            warnLabel={`> ${VOLUME_THRESHOLD}`}
          />
          <StatCard
            label="Resolution Rate"
            value={`${(kpis.resolution_rate * 100).toFixed(1)}%`}
            warn={kpis.resolution_rate < RATE_THRESHOLD}
            warnLabel="< 80%"
          />
        </div>
      )}
    </Panel>
  );
}
